import React, { useEffect, useState } from 'react';
import useCart from '../hooks/useCart';
import { useAllProducts } from '../hooks/useAllProducts';
import { Link } from 'react-router-dom';
import emptyCart from '../assets/img/mobile-empty-cart.png';
import { useTranslation } from 'react-i18next';
import {BASE_URL} from "../config";


const ShoppingCart = ({ showToast }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [cart, setCart] = useState(() => {
        const savedBasket = localStorage.getItem('basket');
        return savedBasket ? JSON.parse(savedBasket) : [];
    });
    const [productDetails, setProductDetails] = useState({});
    const { basket, increment, decrement, remove, clearCart, getTotalItems, getTotalAmount } = useCart();
    const { allProducts, loading, error } = useAllProducts();

    useEffect(() => {
        setCart(() => {
            const savedBasket = localStorage.getItem('basket');
            return savedBasket ? JSON.parse(savedBasket) : [];
        });
        console.log('showToast updated:', showToast);
    }, [basket, showToast]);

    // Fetch product details for items in cart that aren't in the main products list
    useEffect(() => {
        const fetchProductDetails = async () => {
            const cartItems = cart || [];

            // Use allProducts instead of getProductsArray()
            const productsArray = allProducts;

            // Find product IDs that are in cart but not in the current products list
            const missingProductIds = cartItems
                .filter(item => !productsArray.find(p => p.id === item.id))
                .map(item => item.id);

            if (missingProductIds.length === 0) return;

            try {
                // Fetch details for missing products
                for (const productId of missingProductIds) {
                    if (!productDetails[productId]) {
                        const response = await fetch(`${BASE_URL}api/v1/products/${productId}`);
                        if (response.ok) {
                            const productData = await response.json();
                            setProductDetails(prev => ({
                                ...prev,
                                [productId]: productData.data || productData
                            }));
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching product details:', error);
            }
        };

        if (allProducts.length > 0) {
            fetchProductDetails();
        }
    }, [cart, allProducts]); // Use allProducts instead of products

    const toggleCart = () => {
        setIsOpen(!isOpen);
    };

    const closeCart = () => {
        setIsOpen(false);
    };

    const handleIncrement = (item) => {
        // Use the stored price from the cart item or calculate it
        const price = item.price || calculateProductPrice(item.id);
        increment(item.id, price, showToast);
    };

    const handleDecrement = (id) => {
        decrement(id, showToast);
    };

    const handleRemove = (id) => {
        remove(id, showToast);
    };

    // Get product details from either allProducts or fetched details
    const getProductDetails = (productId) => {
        // First try to find in allProducts array
        const productFromAll = allProducts.find(p => p.id === productId);
        if (productFromAll) return productFromAll;

        // Then try to find in fetched product details
        return productDetails[productId];
    };

    // Calculate product price (for items added before price was stored)
    const calculateProductPrice = (productId) => {
        const product = getProductDetails(productId);
        if (!product) return 0;

        return product.discount > 0
            ? product.price - (product.price * product.discount) / 100
            : product.price;
    };

    // Get price for display (with fallback)
    const getItemPrice = (item) => {
        if (item.price !== undefined) {
            return item.price;
        }
        return calculateProductPrice(item.id);
    };

    // Get formatted price for display
    const getFormattedPrice = (price) => {
        if (price === undefined || price === null) return '0';
        return price.toLocaleString();
    };

    const totalItems = cart.reduce((total, item) => total + item.quantity, 0);
    const totalAmount = getTotalAmount();

    return (
        <div className={`shopping-cart d-flex align-items-end flex-column ${isOpen ? 'open' : ''}`}
             style={{ right: isOpen ? 0 : "-350px" }}>

            <div className="cart-bag" onClick={toggleCart}>
                <span id="count-items">{totalItems}</span>
                <button type="button" aria-label='cardButton' className="cart-btn">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="icon icon-tabler icon-tabler-shopping-bag"
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        strokeWidth="1"
                        stroke="#2c3e50"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M6.331 8h11.339a2 2 0 0 1 1.977 2.304l-1.255 8.152a3 3 0 0 1 -2.966 2.544h-6.852a3 3 0 0 1 -2.965 -2.544l-1.255 -8.152a2 2 0 0 1 1.977 -2.304z" />
                        <path d="M9 11v-5a3 3 0 0 1 6 0v5" />
                    </svg>
                </button>
            </div>

            <div className="cart-content" style={{ overflowY: "scroll", maxHeight: "27rem" }}>
                <div className="cart-top position-relative d-flex justify-content-center align-items-center mb-4">
                    <button type="button" aria-label='cardButton' className="close-cart-btn" onClick={closeCart}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="icon icon-tabler icon-tabler-arrow-narrow-right"
                            width="28"
                            height="28"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="#000000"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M5 12l14 0" />
                            <path d="M15 16l4 -4" />
                            <path d="M15 8l4 4" />
                        </svg>
                    </button>
                    <h4 className="cart-title">My Cart</h4>
                </div>

                {loading ? (
                    <div className="text-center">
                        <p>Loading products...</p>
                    </div>
                ) : error ? (
                    <div className="text-center text-danger">
                        <p>Error loading products: {error}</p>
                    </div>
                ) : cart.length === 0 ? (
                    <div className="empty-cart text-center">
                        <h4>{t('Cart is Empty')}</h4>
                        <p>{t('It Seems You Have not Added Any Product To Your Cart Yet')}</p>
                        <img src={emptyCart} alt="Empty Cart" height="64" width="64" />
                        <a href="#products-section" onClick={closeCart}>
                            {t('Shop Now')}
                        </a>
                    </div>
                ) : (
                    <>
                        {cart.map(item => {
                            const itemPrice = getItemPrice(item);
                            const product = getProductDetails(item.id);
                            return (
                                <div key={item.id} className="cart-middle d-flex align-items-center gap-2">
                                    <div className="selected-item-left">
                                        <h6 className="price position-absolute top-0 bg-text mb-0 p-1">
                                            {getFormattedPrice(itemPrice)} IQD
                                        </h6>
                                        {product && product.featuredImage ? (
                                            <img
                                                src={`https://akadgatepharmacy.com/backend/storage/app/public${product.featuredImage.replace('/storage','')}`}
                                                alt={product.name || 'Product'}
                                                className="h-100 w-100"
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/100x100?text=No+Image';
                                                }}
                                            />
                                        ) : (
                                            <div className="h-100 w-100 d-flex align-items-center justify-content-center bg-light">
                                                <span>No Image</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="selected-item-middle d-flex flex-column justify-content-between gap-1">
                                        <h5 className="m-0">{product ? product.name : 'Product'}</h5>
                                        <p className="m-0">{product ? (product.brand?.name || 'No Brand') : 'No Brand'}</p>
                                        <h6 className="m-0 text-center">
                                            {getFormattedPrice(itemPrice * item.quantity)} IQD
                                        </h6>
                                    </div>
                                    <div className="selected-item-right d-flex flex-column justify-content-between gap-1">
                                        <div className="quantity-control text-center">
                                            <button type="button" onClick={() => handleIncrement(item)} className="bg-transparent p-0 border-0">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="icon icon-tabler icon-tabler-chevron-up"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="#000000"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M6 15l6 -6l6 6" />
                                                </svg>
                                            </button>
                                            <h6 className="my-1">{item.quantity}</h6>
                                            <button type="button" onClick={() => handleDecrement(item.id)} className="bg-transparent p-0 border-0">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="icon icon-tabler icon-tabler-chevron-down"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    strokeWidth="1.5"
                                                    stroke="#000000"
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M6 9l6 6l6 -6" />
                                                </svg>
                                            </button>
                                        </div>
                                        <button type="button" onClick={() => handleRemove(item.id)} className="bg-transparent p-0 border-0">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="icon icon-tabler icon-tabler-trash-x"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                strokeWidth="1.5"
                                                stroke="#000000"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M4 7h16" />
                                                <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                                                <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                                                <path d="M10 12l4 4m0 -4l-4 4" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="cart-bottom mt-4 text-center">
                            <button
                                type="button"
                                onClick={clearCart}
                                className="bg-transparent p-0 border-0 text-danger text-decoration-underline"
                            >
                                {t('clear cart')}
                            </button>
                            <h6 className="mt-3 mb-3 fw-bold">
                                {t('SUBTOTAL: ')}{getFormattedPrice(totalAmount)} IQD
                            </h6>
                            <Link to="/cartcheckout" className="proceed-checkout-btn" onClick={closeCart}>
                                {t('PROCEED TO CHECKOUT')}
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ShoppingCart;